/**
 * TEMP-H-015: Polling Fallback for WebSocket Auth Failures
 * 
 * Bounded polling system that activates when WebSocket authentication fails.
 * Provides limited functionality with non-blocking warnings to maintain UX.
 */

import { getApiHeaders } from './api-base';
import { joinApiPath } from './api-config';

export interface PollingConfig {
  executionId: string;
  userId: string;
  onUpdate: (data: any) => void;
  onError: (error: string) => void;
  interval?: number; // milliseconds, default 5000
  maxAttempts?: number; // max failed attempts before giving up, default 10
}

export type PollingStatus = 'active' | 'stopped' | 'error' | 'max_attempts_reached';

/**
 * Bounded polling fallback for when WebSocket auth fails
 */
export class PollingFallback {
  private config: PollingConfig;
  private intervalId: NodeJS.Timeout | null = null;
  private status: PollingStatus = 'stopped';
  private failedAttempts = 0;
  private lastSuccessfulPoll = 0;

  constructor(config: PollingConfig) {
    this.config = {
      interval: 5000, // 5 second default
      maxAttempts: 10,
      ...config
    };
  }

  /**
   * Get interval with jitter to prevent thundering herd
   */
  private getJitteredInterval(): number {
    const baseInterval = this.config.interval || 5000;
    const jitter = Math.random() * 1000; // ±500ms jitter
    return baseInterval + jitter - 500;
  }

  /**
   * Start polling with bounded retry logic
   */
  start(): void {
    if (this.intervalId) {
      this.stop();
    }

    console.warn('🔄 Starting polling fallback due to WebSocket auth failure');
    
    this.status = 'active';
    this.failedAttempts = 0;
    
    // Poll immediately, then on interval
    this.pollOnce();
    
    this.intervalId = setInterval(() => {
      this.pollOnce();
    }, this.getJitteredInterval());
  }

  /**
   * Stop polling
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    
    this.status = 'stopped';
  }

  /**
   * Get current polling status
   */
  getStatus(): PollingStatus {
    return this.status;
  }

  /**
   * Check if polling is currently active
   */
  isActive(): boolean {
    return this.status === 'active' && this.intervalId !== null;
  }

  private async pollOnce(): Promise<void> {
    if (this.status !== 'active') return;

    try {
      // Poll execution status and recent events
      const [executionData, recentEvents] = await Promise.all([
        this.fetchExecutionStatus(),
        this.fetchRecentEvents()
      ]);

      // Merge data and notify
      const updateData = {
        execution: executionData,
        events: recentEvents,
        timestamp: new Date().toISOString(),
        source: 'polling'
      };

      this.config.onUpdate(updateData);
      
      // Reset failed attempts on success
      this.failedAttempts = 0;
      this.lastSuccessfulPoll = Date.now();

    } catch (error) {
      this.handlePollError(error);
    }
  }

  private async fetchExecutionStatus(): Promise<any> {
    const response = await fetch(
      joinApiPath('executions', this.config.executionId, 'status'),
      {
        credentials: 'include',
        headers: getApiHeaders()
      }
    );

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error(`Authentication failed: ${response.status}`);
      }
      throw new Error(`Failed to fetch execution status: ${response.status}`);
    }

    return response.json();
  }

  private async fetchRecentEvents(): Promise<any[]> {
    // Only fetch events from the last successful poll or last 5 minutes
    const since = Math.max(
      this.lastSuccessfulPoll, 
      Date.now() - (5 * 60 * 1000)
    );
    
    const sinceParam = new Date(since).toISOString();
    
    const response = await fetch(
      joinApiPath('executions', this.config.executionId, 'events') + `?since=${sinceParam}&limit=50`,
      {
        credentials: 'include',
        headers: getApiHeaders()
      }
    );

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error(`Authentication failed: ${response.status}`);
      }
      // Don't throw for events - execution status is more critical
      console.warn('Failed to fetch recent events:', response.status);
      return [];
    }

    const data = await response.json();
    return data.events || [];
  }

  private handlePollError(error: unknown): void {
    this.failedAttempts++;
    
    const errorMessage = error instanceof Error ? error.message : 'Unknown polling error';
    
    console.warn(`⚠️ Polling attempt ${this.failedAttempts} failed:`, errorMessage);

    // Check for auth errors - stop immediately
    if (errorMessage.includes('Authentication failed')) {
      this.status = 'error';
      this.stop();
      this.config.onError('Authentication failed during polling. Please refresh the page to log in again.');
      return;
    }

    // Check if we've exceeded max attempts
    if (this.failedAttempts >= (this.config.maxAttempts || 10)) {
      this.status = 'max_attempts_reached';
      this.stop();
      this.config.onError(
        `Polling failed after ${this.failedAttempts} attempts. Please check your connection and refresh the page.`
      );
      return;
    }

    // Continue polling but report non-blocking error
    this.config.onError(
      `Connection issues detected (attempt ${this.failedAttempts}/${this.config.maxAttempts}). Retrying...`
    );
  }

  /**
   * Update configuration (e.g., change execution ID)
   */
  updateConfig(updates: Partial<PollingConfig>): void {
    this.config = { ...this.config, ...updates };
    
    // Reset attempts if execution changed
    if (updates.executionId) {
      this.failedAttempts = 0;
      this.lastSuccessfulPoll = 0;
    }
  }
}

/**
 * Singleton polling manager
 */
class PollingManager {
  private activePolllers = new Map<string, PollingFallback>();

  /**
   * Start polling for a specific execution
   */
  startPolling(executionId: string, config: Omit<PollingConfig, 'executionId'>): PollingFallback {
    // Stop existing polling for this execution
    const existing = this.activePolllers.get(executionId);
    if (existing) {
      existing.stop();
    }

    // Create new polling instance
    const poller = new PollingFallback({
      executionId,
      ...config
    });

    poller.start();
    this.activePolllers.set(executionId, poller);

    return poller;
  }

  /**
   * Stop polling for a specific execution
   */
  stopPolling(executionId: string): void {
    const poller = this.activePolllers.get(executionId);
    if (poller) {
      poller.stop();
      this.activePolllers.delete(executionId);
    }
  }

  /**
   * Stop all active polling
   */
  stopAll(): void {
    this.activePolllers.forEach(poller => poller.stop());
    this.activePolllers.clear();
  }

  /**
   * Get polling status for execution
   */
  getPollingStatus(executionId: string): PollingStatus | null {
    const poller = this.activePolllers.get(executionId);
    return poller ? poller.getStatus() : null;
  }

  /**
   * Get active polling count
   */
  getActiveCount(): number {
    return Array.from(this.activePolllers.values())
      .filter(p => p.isActive()).length;
  }
}

// Export singleton instance
export const pollingManager = new PollingManager();

/**
 * React hook for polling fallback
 */
export function usePollingFallback(
  executionId: string,
  onUpdate: (data: any) => void,
  onError: (error: string) => void,
  userId?: string
) {
  const startPolling = () => {
    return pollingManager.startPolling(executionId, {
      userId: userId || 'anonymous',
      onUpdate,
      onError,
      interval: 5000, // 5 seconds
      maxAttempts: 10
    });
  };

  const stopPolling = () => {
    pollingManager.stopPolling(executionId);
  };

  const getStatus = () => {
    return pollingManager.getPollingStatus(executionId);
  };

  return {
    startPolling,
    stopPolling,
    getStatus,
    isPolling: () => getStatus() === 'active'
  };
}